/**
 * MuiInvoicesList - Optimized Invoices List Page
 *
 * OPTIMIZATION: Phase 4 - Server-side pagination implementation
 *
 * Changes made:
 * - Replaced getInvoices() with usePaginatedData(getInvoicesPaginated) hook
 * - Added debounced search (300ms) - server-side text search across number, providername, refcode, providervat
 * - Analytics data fetched separately via fetchInvoiceAnalytics() to avoid computing from paginated data
 * - Server-side pagination with limit/offset
 * - Server-side sorting by importDate DESC (default)
 *
 * Benefits:
 * - Only loads visible page data (e.g., 25 records instead of 1000+)
 * - Search queries happen on server, reducing client memory usage
 * - Faster initial load and navigation between pages
 */
import React, { useEffect, useState, useMemo, useCallback, memo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Collapse,
  Grid,
  Tooltip,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import ReceiptIcon from "@mui/icons-material/Receipt"
import HomeIcon from "@mui/icons-material/Home"
import BusinessIcon from "@mui/icons-material/Business"
import AssessmentIcon from "@mui/icons-material/Assessment"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import InventoryIcon from "@mui/icons-material/Inventory"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import { getInvoicesPaginated, fetchInvoiceAnalytics } from "../../helpers/api_helper"
import { usePaginatedData } from "../../hooks/usePaginatedData"

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return "€0.00"
  return new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(amount)
}

// Analytics Panel Component
const AnalyticsPanel = memo(({ analytics, expanded, setExpanded, t }) => (
  <Paper
    elevation={0}
    sx={{ mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        bgcolor: "#f8fafc",
        borderBottom: expanded ? "1px solid #e5e7eb" : "none",
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: "#8b5cf6", width: 36, height: 36 }}>
          <AssessmentIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827" }}>
            {t("Invoice Analytics")}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
            {analytics.total} {t("invoices")} • {analytics.providersCount} {t("providers")}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label={formatCurrency(analytics.totalAmount)}
          size="small"
          sx={{ height: 24, fontSize: "0.75rem", fontWeight: 600, bgcolor: "#dcfce7", color: "#166534" }}
        />
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
    </Box>
    <Collapse in={expanded}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: "1px solid #e5e7eb", textAlign: "center" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                <ReceiptIcon sx={{ fontSize: 20, color: "#8b5cf6" }} />
              </Stack>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#8b5cf6" }}>
                {analytics.total}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Total Invoices")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: "1px solid #e5e7eb", textAlign: "center" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                <InventoryIcon sx={{ fontSize: 20, color: "#3b82f6" }} />
              </Stack>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#3b82f6" }}>
                {analytics.totalItems}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Total Items")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: "1px solid #e5e7eb", textAlign: "center" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                <AccountBalanceIcon sx={{ fontSize: 20, color: "#10b981" }} />
              </Stack>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#10b981" }}>
                {formatCurrency(analytics.totalVat)}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Total VAT")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: "1px solid #e5e7eb", textAlign: "center" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
              </Stack>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#f59e0b" }}>
                {formatCurrency(analytics.totalAmount)}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Total Value")}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Top Providers */}
        {analytics.topProviders.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
              {t("Top Providers")}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {analytics.topProviders.map((p) => (
                <Chip
                  key={p.name}
                  icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                  label={`${p.name}: ${p.count}`}
                  size="small"
                  sx={{ height: 26, fontSize: "0.75rem", bgcolor: "#f3f4f6", color: "#374151" }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Monthly Breakdown */}
        {analytics.byMonth.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
              {t("Recent Months")}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {analytics.byMonth.slice(0, 6).map((m) => (
                <Chip
                  key={m.month}
                  icon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
                  label={`${m.month}: ${formatCurrency(m.total)}`}
                  size="small"
                  sx={{ height: 26, fontSize: "0.75rem", bgcolor: "#ede9fe", color: "#6b21a8" }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Collapse>
  </Paper>
))

const MuiInvoicesList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  document.title = t("Invoices") + " | FlexFlow ERP"

  // Analytics state (fetched separately from paginated data)
  const [analytics, setAnalytics] = useState({
    total: 0,
    totalAmount: 0,
    totalVat: 0,
    totalSubtotal: 0,
    totalItems: 0,
    providersCount: 0,
    topProviders: [],
    byMonth: []
  })
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false)

  // ============================================================================
  // SERVER-SIDE PAGINATION via usePaginatedData hook
  // ============================================================================
  const {
    data: invoices,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    refresh
  } = usePaginatedData(getInvoicesPaginated, {
    initialPageSize: 25,
    debounceMs: 300,
    autoFetch: true
  })

  // ============================================================================
  // FETCH ANALYTICS SEPARATELY (lightweight query)
  // ============================================================================
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    try {
      const data = await fetchInvoiceAnalytics()
      setAnalytics(data)
    } catch (err) {
      console.error("Error loading analytics:", err)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  // Load analytics on mount and when location changes (e.g., after creating an invoice)
  useEffect(() => {
    loadAnalytics()
  }, [location.key, loadAnalytics])

  // ============================================================================
  // PROCESS INVOICE DATA (parse totals JSON for display)
  // ============================================================================
  const processedInvoices = useMemo(() => {
    return invoices.map((invoice) => {
      let parsedTotals = {}
      try {
        parsedTotals = JSON.parse(invoice.totals || "{}")
      } catch (e) {
        parsedTotals = {}
      }
      return {
        ...invoice,
        totalOrder: parsedTotals.totalOrder || 0,
        totalVat: parsedTotals.totalVat || 0,
        totalPrdAmmount: parsedTotals.totalPrdAmmount || 0,
        totalItems: parsedTotals.totalQuantity || 0,
      }
    })
  }, [invoices])

  const handleRowClick = (invoice) => {
    navigate(`/invoice-detail/${invoice.id}`)
  }

  const handleRefresh = useCallback(() => {
    refresh()
    loadAnalytics()
  }, [refresh, loadAnalytics])

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#6b7280" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", "&:hover": { color: "#3b82f6" } }}
            >
              <HomeIcon sx={{ fontSize: 16 }} />
            </Link>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center" }}>
              <ReceiptIcon sx={{ fontSize: 16, mr: 0.5, color: "#8b5cf6" }} />
              {t("Invoices")}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <Tooltip title={t("Refresh")}>
          <IconButton size="small" onClick={handleRefresh} sx={{ color: "#6b7280" }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Analytics Panel */}
      <AnalyticsPanel
        analytics={analytics}
        expanded={analyticsExpanded}
        setExpanded={setAnalyticsExpanded}
        t={t}
      />

      {/* Search & Table */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {/* Search Bar */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e7eb", bgcolor: "#f9fafb" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder={t("Search by invoice #, provider, AFM, code...")}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 320,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  borderRadius: 1,
                  "& fieldset": { borderColor: "#e5e7eb" },
                  "&:hover fieldset": { borderColor: "#d1d5db" },
                  "&.Mui-focused fieldset": { borderColor: "#8b5cf6" },
                },
              }}
            />
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: "auto" }}>
              {total} {t("results")}
            </Typography>
          </Stack>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} sx={{ color: "#8b5cf6" }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Invoice #")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Date")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Provider")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Code")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Items")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Subtotal")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("VAT")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151", textTransform: "uppercase", py: 1.5 }}>
                      {t("Total")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedInvoices.length > 0 ? (
                    processedInvoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        hover
                        onClick={() => handleRowClick(invoice)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#faf5ff" },
                          "& td": { borderBottom: "1px solid #f3f4f6" },
                        }}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                              <ReceiptIcon sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#8b5cf6" }}>
                              {invoice.number || "-"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {invoice.importDate ? moment(invoice.importDate).format("DD/MM/YYYY") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <BusinessIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                            <Box>
                              <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}>
                                {invoice.providername || "-"}
                              </Typography>
                              {invoice.providervat && (
                                <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                                  AFM: {invoice.providervat}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {invoice.refcode ? (
                            <Chip
                              label={invoice.refcode}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                bgcolor: "#fef3c7",
                                color: "#92400e",
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: "0.813rem", color: "#9ca3af" }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Chip
                            label={invoice.totalItems || 0}
                            size="small"
                            sx={{
                              height: 22,
                              minWidth: 32,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              bgcolor: "#dbeafe",
                              color: "#1d4ed8",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {formatCurrency(invoice.totalPrdAmmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: invoice.totalVat > 0 ? "#16a34a" : "#9ca3af", fontWeight: invoice.totalVat > 0 ? 500 : 400 }}>
                            {formatCurrency(invoice.totalVat || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>
                            {formatCurrency(invoice.totalOrder || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <ReceiptIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                        <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                          {searchTerm ? t("No invoices found matching your search") : t("No invoices found")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={pagination.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("Rows")}
              sx={{
                borderTop: "1px solid #e5e7eb",
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  fontSize: "0.813rem",
                  color: "#6b7280",
                },
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  )
}

export default MuiInvoicesList
